import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLiveUpdate } from "@/contexts/LiveUpdateContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ClipboardList, CheckCircle2, Clock, AlertTriangle, BarChart3, Users, Activity } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const STATUS_COLORS = { todo: "#64748b", in_progress: "#3b82f6", review: "#f59e0b", done: "#10b981" };
const STATUS_LABELS = { todo: "To Do", in_progress: "In Progress", review: "Review", done: "Selesai" };
const DIVISION_LABELS = {
  sistem_informasi: "Sistem Informasi", infrastruktur_ti: "Infrastruktur TI",
  pelayanan: "Pelayanan", tata_kelola: "Tata Kelola",
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [divisionStats, setDivisionStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { updateTrigger } = useLiveUpdate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const requests = [
          API.get("/dashboard/stats"),
          API.get("/logs?limit=8"),
        ];
        if (user?.role === "ka_div" && user?.division) {
          requests.push(API.get(`/dashboard/stats?division=${user.division}`));
        }
        const [statsRes, logsRes, divRes] = await Promise.all(requests);
        setStats(statsRes.data);
        setRecentLogs(logsRes.data.data || logsRes.data || []);
        if (divRes) setDivisionStats(divRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [updateTrigger, user]);

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const statusData = (stats && stats.by_status) ? Object.entries(stats.by_status).map(([key, val]) => ({
    name: STATUS_LABELS[key] || key, value: val, fill: STATUS_COLORS[key] || "#94a3b8",
  })) : [];

  const priorityData = stats?.by_priority ? [
    { name: "Tinggi", value: stats.by_priority.high || 0, fill: "#ef4444" },
    { name: "Sedang", value: stats.by_priority.medium || 0, fill: "#f97316" },
    { name: "Rendah", value: stats.by_priority.low || 0, fill: "#3b82f6" },
  ] : [];

  const summaryCards = stats ? [
    { label: "Total Tugas", value: stats.total_tasks || 0, icon: ClipboardList, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-50 dark:bg-blue-900/30" },
    { label: "Tugas Aktif", value: stats.active_tasks || 0, icon: Clock, color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-50 dark:bg-amber-900/30" },
    { label: "Selesai", value: stats.completed_tasks || 0, icon: CheckCircle2, color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-900/30" },
    { label: "Prioritas Tinggi", value: stats?.by_priority?.high || 0, icon: AlertTriangle, color: "text-red-600 dark:text-red-400", bg: "bg-red-50 dark:bg-red-900/30" },
  ] : [];

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Dasbor
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Ringkasan kemajuan proyek tim Anda</p>
      </div>

      {/* Division Stats untuk Ka Div */}
      {user?.role === "ka_div" && divisionStats && (
        <div className="mb-8 p-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10">
          <p className="text-xs font-bold tracking-[0.15em] uppercase text-blue-600 dark:text-blue-400 mb-3">
            Divisi {DIVISION_LABELS[user.division] || user.division}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Total", value: divisionStats.total_tasks, color: "text-slate-700 dark:text-slate-200" },
              { label: "Aktif", value: divisionStats.active_tasks, color: "text-amber-600 dark:text-amber-400" },
              { label: "Selesai", value: divisionStats.completed_tasks, color: "text-emerald-600 dark:text-emerald-400" },
              { label: "Prioritas Tinggi", value: divisionStats.by_priority?.high || 0, color: "text-red-600 dark:text-red-400" },
            ].map((item) => (
              <div key={item.label} className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
                <p className="text-xs text-slate-500 dark:text-slate-400">{item.label}</p>
                <p className={`text-2xl font-black mt-0.5 ${item.color}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="summary-cards">
        {summaryCards.map((card) => (
          <Card key={card.label} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-none hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:hover:shadow-[0_8px_30px_rgba(255,255,255,0.02)] hover:-translate-y-0.5 transition-all duration-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 dark:text-slate-400">{card.label}</p>
                  <p className="text-3xl font-black text-slate-900 dark:text-white mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{card.value}</p>
                </div>
                <div className={`w-11 h-11 rounded-lg ${card.bg} flex items-center justify-center`}>
                  <card.icon className={`w-5 h-5 ${card.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Status distribution */}
        <Card className="lg:col-span-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Tugas Berdasarkan Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[260px]" data-testid="status-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statusData} barSize={40}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" className="dark:opacity-20" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} className="dark:opacity-80" />
                  <YAxis tick={{ fontSize: 12, fill: "#64748b" }} allowDecimals={false} className="dark:opacity-80" />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px", backgroundColor: "rgba(255,255,255,0.9)", color: "#333" }} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {statusData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Priority pie */}
        <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Berdasarkan Prioritas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]" data-testid="priority-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={priorityData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value" stroke="none">
                    {priorityData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px", backgroundColor: "rgba(255,255,255,0.9)", color: "#333" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              {priorityData.map((p) => (
                <div key={p.name} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: p.fill }} />
                  {p.name} ({p.value})
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workload + Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Team Workload */}
        <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Beban Kerja Tim
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="workload-section">
            {stats.workload.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400 py-4">Tidak ada tugas aktif yang ditugaskan</p>
            ) : (
              <div className="space-y-3">
                {stats.workload.map((w) => (
                  <div key={w.name} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-600 dark:text-slate-300 shrink-0">
                      {(w.name || "Unknown").split(" ").map(n => n && n[0]).join("").toUpperCase().slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">{w.name}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">{w.tasks} tugas</span>
                      </div>
                      <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 dark:bg-blue-400 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min((w.tasks / 5) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-none">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Activity className="w-5 h-5 text-emerald-500 dark:text-emerald-400" />
              Aktivitas Terbaru
            </CardTitle>
            <button
              onClick={() => navigate("/activity")}
              className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
              data-testid="view-all-activity"
            >
              Lihat semua
            </button>
          </CardHeader>
          <CardContent data-testid="recent-activity">
            {recentLogs.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400 py-4">Tidak ada aktivitas terbaru</p>
            ) : (
              <div className="space-y-0 relative">
                <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-200 dark:bg-slate-700" />
                {recentLogs.slice(0, 6).map((log) => (
                  <div key={log.id} className="flex items-start gap-3 py-2 relative">
                    <div className="w-3.5 h-3.5 rounded-full bg-slate-300 dark:bg-slate-600 border-2 border-white dark:border-slate-800 z-10 mt-1 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-slate-700 dark:text-slate-300 leading-snug">{log.action}</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                        {new Date(log.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
